/* Recommendations belong to a mounted drawer, never a shared async store. */
(() => {
  'use strict';
  const cache = new Map();
  let mounted;
  const ids = root => new Set((root.dataset.cartProducts || '').split(',').filter(Boolean));
  const cards = root => [...root.querySelectorAll('#cart-upsell-drawer > [data-mw-upsell-product]')];
  const productId = card => card.dataset.mwUpsellProduct;
  const signature = root => [...ids(root)].sort().join(',');

  function dispose() {
    if (!mounted) return;
    mounted.controller?.abort();
    mounted.version++;
    mounted.root.querySelector('.splide')?.splide?.destroy();
    mounted = null;
  }

  async function reconcile(fresh) {
    const source = fresh.matches?.('#mini-cart-upsell') ? fresh : fresh.querySelector('#mini-cart-upsell');
    const state = mounted;
    if (!source || !state || !state.root.isConnected) return;
    const nextSignature = signature(source);
    if (state.signature === nextSignature) return;
    state.signature = nextSignature;
    state.controller?.abort();
    state.controller = new AbortController();
    const version = ++state.version;
    const current = () => mounted === state && state.version === version && state.root.isConnected;
    const excluded = ids(source);
    const limit = Math.max(0, Number(source.dataset.upsellLimit) || 0);
    const list = state.root.querySelector('#cart-upsell-drawer');
    if (!list) return;
    const slider = state.root.querySelector('.splide')?.splide;
    const oldIndex = slider?.index || 0;
    const activeId = productId(cards(state.root)[oldIndex] || { dataset: {} });
    let changed = false;
    for (const card of cards(state.root)) {
      if (!excluded.has(productId(card))) continue;
      card.remove();
      changed = true;
    }
    const append = card => {
      const id = productId(card);
      if (!id || excluded.has(id) || cards(state.root).some(item => productId(item) === id) || cards(state.root).length >= limit) return;
      const node = card.cloneNode(true);
      list.appendChild(node);
      changed = true;
    };
    // Server-rendered metafield/configured products always fill gaps first.
    cards(source).forEach(append);
    try {
      for (const id of excluded) {
        if (!current() || cards(state.root).length >= limit) break;
        const url = `${source.dataset.recommendationsUrl}&product_id=${encodeURIComponent(id)}&limit=10&intent=related`;
        let html = cache.get(url);
        if (!html) {
          const response = await fetch(url, { signal: state.controller.signal });
          if (!response.ok) throw new Error(`Recommendations failed: ${response.status}`);
          html = await response.text();
          if (!current()) return;
          cache.set(url, html);
        }
        if (!current()) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('template[x-teleport="#cart-upsell-drawer"]').forEach(template => {
          const card = template.content.querySelector('[data-mw-upsell-product]');
          if (card) append(card);
        });
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('Cart recommendations unavailable:', error);
    } finally {
      if (current()) {
        state.root.dataset.cartProducts = source.dataset.cartProducts;
        const remaining = cards(state.root);
        if (changed) {
          const instance = state.root.querySelector('.splide')?.splide;
          instance?.refresh();
          const index = remaining.findIndex(card => productId(card) === activeId);
          instance?.go(index >= 0 ? index : Math.max(0, Math.min(oldIndex, remaining.length - 1)));
        }
        document.dispatchEvent(new CustomEvent('mw:cart:upsells-updated', { detail: { products: remaining.map(productId) } }));
      }
    }
  }
  window.mwCartUpsells = {
    mount(root) {
      if (mounted?.root === root) return;
      dispose();
      mounted = { root, version: 0, signature: null };
      reconcile(root);
    },
    reconcile, dispose
  };
})();
